// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type CreateTableViewRequest,
  type TableView,
  type TableViewId,
  type TableViewType,
  type TableViewWithGridState,
  type UpdateTableViewRequest,
  useEvolveDelete,
  useEvolveGet,
  useEvolvePatch,
  useEvolvePost,
} from '@evolvemep/api-ts';

import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import type { StrongOmit } from 'helpers/helper-types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useUserPreferences } from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';
import { getLocalStorageWrapped, setLocalStorageWrapped } from 'hooks/useLocalStorage';

/** Internal provider value that manages all table view types. */
type TableViewsContextValue = {
  ensureTableViewsLoadedFor: (tableViewType: TableViewType) => Promise<void>;
  getTableViewsForType: (tableViewType: TableViewType) => TableView[];
  createTableViewForType: (
    tableViewType: TableViewType,
    newTableView: StrongOmit<CreateTableViewRequest, 'tableViewType'>,
  ) => Promise<TableViewWithGridState>;
  updateTableViewForType: (
    tableViewType: TableViewType,
    tableViewId: TableViewId,
    updatedTableView: UpdateTableViewRequest,
  ) => Promise<TableViewWithGridState>;
  deleteTableViewForType: (tableViewType: TableViewType, tableViewId: TableViewId) => Promise<void>;
  getSelectedForType: (tableViewType: TableViewType) => TableViewId | null;
  setSelectedForType: (tableViewType: TableViewType, tableViewId: TableViewId | null) => void;
  getLoadingForType: (tableViewType: TableViewType) => boolean;
  getMutationRunningForType: (tableViewType: TableViewType) => boolean;
};

const TableViewsContext = createContext<TableViewsContextValue | undefined>(undefined);

export const TableViewsProvider = ({ children }: { children: ReactNode }) => {
  const [tableViewsByType, setTableViewsByType] = useState<Record<TableViewType, TableView[]>>({});
  const [selectedByType, setSelectedByType] = useState<Record<TableViewType, TableViewId | null>>({});
  const [loadingByType, setLoadingByType] = useState<Record<TableViewType, boolean>>({});
  const [mutationsRunningByType, setMutationsRunningByType] = useState<
    Record<TableViewType, { creating: boolean; updating: boolean; deleting: boolean }>
  >({});

  const getMutationRunningForType = useCallback(
    (tableViewType: TableViewType) => {
      const { creating, updating, deleting } = mutationsRunningByType[tableViewType] ?? {
        creating: false,
        updating: false,
        deleting: false,
      };
      return creating || updating || deleting;
    },
    [mutationsRunningByType],
  );
  const setMutationRunningForType = useCallback(
    (tableViewType: TableViewType, mutationType: 'creating' | 'updating' | 'deleting', running: boolean) => {
      setMutationsRunningByType((prev) => {
        const prevForType = prev[tableViewType] ?? { creating: false, updating: false, deleting: false };
        return {
          ...prev,
          [tableViewType]: { ...prevForType, [mutationType]: running },
        };
      });
    },
    [],
  );

  // Get the initial table views for a given type.
  const initializedByType = useRef<Record<TableViewType, boolean>>({});
  const { apiCall: getTableViewsApiCall } = useEvolveGet(`admin/tableView`);
  const ensureTableViewsLoadedFor = useCallback(
    async (tableViewType: TableViewType) => {
      if (tableViewType === TABLE_VIEW_TYPE.UNKNOWN) return;
      if (initializedByType.current[tableViewType]) return;
      initializedByType.current[tableViewType] = true;

      const cachedTableViews = getLocalStorageWrapped(`TABLE_VIEWS_${tableViewType}`);
      if (cachedTableViews) {
        setTableViewsByType((prev) => ({ ...prev, [tableViewType]: cachedTableViews }));
      }

      setLoadingByType((prev) => ({ ...prev, [tableViewType]: true }));
      const loadedTableViews = await getTableViewsApiCall({ params: { tableViewType } });
      setTableViewsByType((prev) => ({ ...prev, [tableViewType]: loadedTableViews }));
      setLocalStorageWrapped(`TABLE_VIEWS_${tableViewType}`, loadedTableViews);
      setLoadingByType((prev) => ({ ...prev, [tableViewType]: false }));
    },
    [getTableViewsApiCall],
  );

  const { apiCall: createTableViewApiCall } = useEvolvePost('admin/tableView');
  const createTableViewForType = useCallback(
    async (tableViewType: TableViewType, newTableView: StrongOmit<CreateTableViewRequest, 'tableViewType'>) => {
      setMutationRunningForType(tableViewType, 'creating', true);
      const createdTableView = await createTableViewApiCall({ ...newTableView, tableViewType }).finally(() =>
        setMutationRunningForType(tableViewType, 'creating', false),
      );
      setTableViewsByType((prev) => {
        const prevList = prev[tableViewType] ?? [];
        return {
          ...prev,
          [tableViewType]: [...prevList, createdTableView].toSorted((a, b) => a.name.localeCompare(b.name)),
        };
      });
      return createdTableView;
    },
    [createTableViewApiCall, setMutationRunningForType],
  );

  const { apiCall: updateTableViewApiCall } = useEvolvePatch('admin/tableView/:tableViewId');
  const updateTableViewForType = useCallback(
    async (tableViewType: TableViewType, tableViewId: TableViewId, updatedTableView: UpdateTableViewRequest) => {
      setMutationRunningForType(tableViewType, 'updating', true);
      const tableView = await updateTableViewApiCall(
        { ...updatedTableView },
        { url: `admin/tableView/${tableViewId}` },
      ).finally(() => setMutationRunningForType(tableViewType, 'updating', false));
      setTableViewsByType((prev) => {
        const prevList = prev[tableViewType] ?? [];
        const index = prevList.findIndex((tv) => tv.tableViewId === tableViewId);
        if (index === -1) return prev;
        const newList = [...prevList];
        newList[index] = tableView;
        return { ...prev, [tableViewType]: newList.toSorted((a, b) => a.name.localeCompare(b.name)) };
      });
      return tableView;
    },
    [updateTableViewApiCall, setMutationRunningForType],
  );

  const { apiCall: deleteTableViewApiCall } = useEvolveDelete('admin/tableView/:tableViewId');
  const deleteTableViewForType = useCallback(
    async (tableViewType: TableViewType, tableViewId: TableViewId) => {
      setMutationRunningForType(tableViewType, 'deleting', true);
      await deleteTableViewApiCall({ url: `admin/tableView/${tableViewId}` });
      setMutationRunningForType(tableViewType, 'deleting', false);
      setTableViewsByType((prev) => {
        const prevList = prev[tableViewType] ?? [];
        return { ...prev, [tableViewType]: prevList.filter((tv) => tv.tableViewId !== tableViewId) };
      });
    },
    [deleteTableViewApiCall, setMutationRunningForType],
  );

  const getTableViewsForType = useCallback(
    (tableViewType: TableViewType) => tableViewsByType[tableViewType] ?? [],
    [tableViewsByType],
  );

  const getSelectedForType = useCallback(
    (tableViewType: TableViewType) => selectedByType[tableViewType] ?? null,
    [selectedByType],
  );
  const setSelectedForType = useCallback((tableViewType: TableViewType, tableViewId: TableViewId | null) => {
    setSelectedByType((prev) => ({ ...prev, [tableViewType]: tableViewId }));
  }, []);

  const getLoadingForType = useCallback(
    (tableViewType: TableViewType) => loadingByType[tableViewType] ?? false,
    [loadingByType],
  );

  const value: TableViewsContextValue = useMemo(
    () => ({
      ensureTableViewsLoadedFor,
      getTableViewsForType,
      createTableViewForType,
      updateTableViewForType,
      deleteTableViewForType,
      getSelectedForType,
      setSelectedForType,
      getLoadingForType,
      getMutationRunningForType,
    }),
    [
      ensureTableViewsLoadedFor,
      getTableViewsForType,
      createTableViewForType,
      updateTableViewForType,
      deleteTableViewForType,
      getSelectedForType,
      setSelectedForType,
      getLoadingForType,
      getMutationRunningForType,
    ],
  );

  return <TableViewsContext.Provider value={value}>{children}</TableViewsContext.Provider>;
};

export const useTableViews = (tableViewType: TableViewType) => {
  const provider = useGeneralContext(TableViewsContext, 'TableViews');

  useEffect(() => {
    // Load the initial table views for the given type.
    void provider.ensureTableViewsLoadedFor(tableViewType);
  }, [provider, tableViewType]);

  // NOTE: This is set to lazy as the default table view is loaded by useServerSideGrid and an additional fetch here is redundant.
  // We just need the ability to update the default table view ID from this hook, which is why we still use the hook but with lazy loading.
  const [
    defaultTableViewId,
    onChangeDefaultTableViewId,
    loadingDefaultTableViewId,
    { mutationRunning: defaultTableViewMutationRunning },
  ] = useUserPreferences(`DEFAULT_TABLE_VIEW_ID_${tableViewType}`, null, true);

  const selectedTableViewId = provider.getSelectedForType(tableViewType);
  const setSelectedTableViewId = useCallback(
    (tableViewId: TableViewId | null) => {
      provider.setSelectedForType(tableViewType, tableViewId);
    },
    [provider, tableViewType],
  );

  const tableViews = provider.getTableViewsForType(tableViewType);

  const createTableView = useCallback(
    async (newTableView: StrongOmit<CreateTableViewRequest, 'tableViewType'>) =>
      provider.createTableViewForType(tableViewType, newTableView),
    [provider, tableViewType],
  );

  const updateTableView = useCallback(
    async (tableViewId: TableViewId, updatedTableView: UpdateTableViewRequest) =>
      provider.updateTableViewForType(tableViewType, tableViewId, updatedTableView),
    [provider, tableViewType],
  );

  const deleteTableView = useCallback(
    async (tableViewId: TableViewId) => provider.deleteTableViewForType(tableViewType, tableViewId),
    [provider, tableViewType],
  );

  const loading = provider.getLoadingForType(tableViewType) || loadingDefaultTableViewId;

  const mutationRunning = provider.getMutationRunningForType(tableViewType) || defaultTableViewMutationRunning;

  return {
    selectedTableViewId,
    setSelectedTableViewId,
    tableViews,
    defaultTableViewId,
    onChangeDefaultTableViewId,
    loading,
    createTableView,
    updateTableView,
    deleteTableView,
    mutationRunning,
  };
};
